import { gql } from '@apollo/client';
import { ChevronDownIcon, CircleIcon, InfoIcon, LoadingIcon } from '@assets/icons';
import { SearchAndFilter } from '@components/filter';
import { Group } from '@components/layout';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  useGetAvailableInstanceTypesWithDetailsQuery,
  useGetInstanceTypesSearchDetailQuery,
  type InstanceTypeDetails,
  type InstanceTypesFilter,
} from '@generated/graphql';
import { useSearchDetails } from '@hooks/SearchDetails';
import { Box, Popover, Stack, useTheme } from '@mui/material';
import { useField } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';

gql`
  query GetInstanceTypesSearchDetail {
    getInstanceTypesSearchDetail {
      workloadTypes {
        value
        display
      }
      gpuTypes {
        value
        display
      }
      cpuCount {
        value
        display
      }
      ramGB {
        value
        display
      }
      vramGB {
        value
        display
      }
    }
  }
`;

gql`
  query GetAvailableInstanceTypesWithDetails($filters: InstanceTypesFilter) {
    getInstanceTypesWithDetails(filters: $filters) {
      instanceType
      workloadType
      cpuCount
      cpuClockSpeed
      gpuType
      ramGB
      vramGB
      gpuAvailable
      preferred
      hourlyPrice
    }
  }
`;

export const instanceTypeSchema = yup.string().required('Instance type is required');

export const DefaultOption = 't3.xlarge';

export const InstanceSelector = ({
  name,
  label,
  labelInfo,
}: {
  name: string;
  label: string;
  labelInfo?: string;
}) => {
  const { palette } = useTheme();

  const { filter, queryFilter, setFilter } = useSearchDetails<InstanceTypesFilter>({
    cpuCount: { type: 'numberRange', value: [], display: [], label: 'CPU count' },
    gpuTypes: { type: 'exact', value: [], display: [], label: 'GPU types' },
    gpuAvailable: { type: 'boolean', value: false, label: 'GPU available' },
    ramGB: { type: 'numberRange', value: [], display: [], label: 'RAM (GB)' },
    vramGB: { type: 'numberRange', value: [], display: [], label: 'VRAM (GB)' },
    workloadTypes: { type: 'exact', value: [], display: [], label: 'Workload types' },
    texts: { type: 'string', value: [], display: [] },
  });

  const { data } = useGetInstanceTypesSearchDetailQuery();
  const searchDetail = data?.getInstanceTypesSearchDetail;

  const { data: instanceTypesData, loading: instanceTypesLoading } =
    useGetAvailableInstanceTypesWithDetailsQuery({
      variables: {
        filters: queryFilter,
      },
    });
  const instanceTypes = instanceTypesData?.getInstanceTypesWithDetails || [];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClose = () => setAnchorEl(null);

  const [field, , helpers] = useField<string>(name);

  const selectedInstanceType =
    instanceTypes.find((instanceType) => instanceType.instanceType === field.value) || null;

  return (
    <>
      <Stack width="100%" mb={5}>
        <Typography
          className="deckard-text-input-label"
          component="label"
          htmlFor={name}
          mb={1}
          variant="caption2"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {label}
          {labelInfo && (
            <Tooltip title={labelInfo}>
              <InfoIcon size={10} />
            </Tooltip>
          )}
        </Typography>
        <Group
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{
            backgroundColor: palette.input.background.default,
            paddingY: 1,
            paddingX: 2,
            borderRadius: 1,
            minHeight: '34px',
            cursor: 'pointer',
            border: `1px solid ${palette.input.border.default}`,
            '&:hover': {
              backgroundColor: palette.input.background.hover,
            },
            '&:active': {
              backgroundColor: palette.input.background.active,
            },
            '&:disabled': {
              backgroundColor: palette.input.background.disabled,
              cursor: 'not-allowed',
            },
          }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {!selectedInstanceType ? (
            <InstanceTypeDisplaySkeleton />
          ) : (
            <InstanceTypeDisplay instanceType={selectedInstanceType} />
          )}
          <ChevronDownIcon size={16} />
        </Group>
      </Stack>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              maxWidth: '80%',
              width: '475px',
              backgroundColor: palette.input.background.default,
              backgroundImage: 'none',
              border: `1px solid ${palette.input.border.default}`,
              pt: 2,
            },
          },
        }}
      >
        <SearchAndFilter
          context="instance-selector"
          filter={filter}
          searchDetail={searchDetail}
          setFilter={setFilter}
          sx={{ px: 3, py: 3 }}
        />
        <Stack height="350px" sx={{ overflowY: 'auto' }}>
          {instanceTypesLoading ? (
            <Stack alignItems="center" justifyContent="center" height="100%" py={4}>
              <LoadingIcon size={16} />
            </Stack>
          ) : instanceTypes.length > 0 ? (
            instanceTypes.map((instanceType) => (
              <Box
                key={instanceType.instanceType}
                sx={{
                  p: 3,
                  '&:hover': { backgroundColor: palette.input.background.hover, cursor: 'pointer' },
                  border: `1px solid ${instanceType.preferred ? palette.primary.main : 'transparent'}`,
                  borderRadius: 1,
                }}
                onClick={() => {
                  void helpers.setValue(instanceType.instanceType);
                  handleClose();
                }}
              >
                {instanceType.preferred && (
                  <Box
                    sx={{
                      width: 'fit-content',
                      borderBottomRightRadius: 1,
                      backgroundColor: palette.primary.main,
                      color: palette.primary.contrastText,
                      ml: -3,
                      mt: -3,
                      mb: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    {`Recommended for ${instanceType.gpuAvailable ? 'GPU' : 'CPU'}`}
                  </Box>
                )}
                <InstanceTypeDisplay key={instanceType.instanceType} instanceType={instanceType} />
              </Box>
            ))
          ) : (
            <Stack alignItems="center" justifyContent="center" height="100%" pt={4} pb={8}>
              <Typography variant="body2">No instances match your search criteria.</Typography>
            </Stack>
          )}
        </Stack>
      </Popover>
    </>
  );
};

const InstanceTypeDisplaySkeleton = () => {
  return (
    <Group justifyContent="space-between">
      <Stack gap={1}>
        <Typography variant="subtitle1" sx={{ color: 'text.disabled' }}>
          Select an instance type
        </Typography>
      </Stack>
    </Group>
  );
};

const InstanceTypeDisplay = ({ instanceType }: { instanceType: InstanceTypeDetails }) => {
  return (
    <Group justifyContent="space-between" flex={1}>
      <Stack gap={1}>
        <Typography variant="subtitle1">
          {instanceType.instanceType}
          {instanceType.preferred ? ' (Recommended)' : ''}
        </Typography>
        <Group alignItems="center" gap={1}>
          <Typography variant="caption2">{instanceType.cpuCount} vCPU</Typography>
          <CircleIcon size={4} />
          {instanceType.ramGB && (
            <>
              <Typography variant="caption2">{instanceType.ramGB}GB ram</Typography>
              <CircleIcon size={4} />
            </>
          )}
          {instanceType.vramGB && (
            <>
              <Typography variant="caption2">{instanceType.vramGB}GB vram</Typography>
              <CircleIcon size={4} />
            </>
          )}
          <Typography variant="caption2" sx={{ textTransform: 'capitalize' }}>
            {instanceType.workloadType}
          </Typography>
        </Group>
      </Stack>
      <Stack>
        <Typography variant="body2">${instanceType.hourlyPrice} / hr</Typography>
      </Stack>
    </Group>
  );
};
